import { createSlice, type PayloadAction, type SliceCaseReducers } from "@reduxjs/toolkit";
import { type MidnightActionPayload } from "interfaces";
import { type EntityState } from "interfaces/state/entityState";
import dateTimeUtils from "utils/dateTimeUtils";
import { type EntityDetailsPayload } from "../reducers/hoc/withEntityState";
import { type CreateSlicePayload, getPrefix } from "./models";

const getStateOfPublishOrRevertSuccess = (state: EntityState, entityId: number): EntityState => {
  if (!state.changingEntityState) {
    return { ...state };
  }
  return {
    ...state,
    lastModifiedDateTime: dateTimeUtils.getEmptyDate(),
    changingEntityState: false,
    isPublished: true,
    entityId,
  };
};

export const createEntityStateSlice = <
  Reducers extends SliceCaseReducers<TEntityState>,
  TEntityState extends EntityState = EntityState,
>({
  namePayload,
  initialState,
  reducers,
}: CreateSlicePayload<TEntityState, Reducers>) => {
  return createSlice({
    name: getPrefix(namePayload),
    initialState,
    reducers: {
      beginAction: (state) => {
        state.changingEntityState = true;
        state.error = undefined;
      },
      successAction: (state, action: PayloadAction<MidnightActionPayload>) => {
        if (state.entityId > 0 && state.entityId !== action.payload.entityId) {
          return;
        }
        state.changingEntityState = false;
        state.isPublished = false;
        state.entityId = action.payload.entityId!;
      },
      failureAction: (state, action: PayloadAction<MidnightActionPayload>) => {
        state.changingEntityState = false;
        state.error = action.payload.error;
      },
      createDraftSuccess: (state, action: PayloadAction<MidnightActionPayload>) => {
        if (state.entityId > 0 && state.entityId !== action.payload.entityId) {
          return;
        }
        state.changingEntityState = false;
        state.isPublished = false;
        state.entityId = action.payload.entityId!;
        state.lastModifiedDateTime = dateTimeUtils.getCurrentDate()?.toISOString();
      },
      publishSuccess: (state, action: PayloadAction<MidnightActionPayload>) => {
        getStateOfPublishOrRevertSuccess(state, action.payload.entityId as number);
      },
      revertSuccess: (state, action: PayloadAction<MidnightActionPayload>) => {
        getStateOfPublishOrRevertSuccess(state, action.payload.id as number);
      },
      resetState: () => initialState,
      updateBegin: (state) => {
        state.isEntityCommandInProgress = true;
      },
      updateSuccess: (state, action: PayloadAction<MidnightActionPayload>) => {
        if (action.payload.entityId == state.entityId) {
          state.isEntityCommandInProgress = false;
          state.lastModifiedDateTime = dateTimeUtils.getCurrentDate()?.toISOString();
        }
      },
      resetUpdate: (state) => {
        state.isEntityCommandInProgress = false;
      },
      setEntityId: (state, action: PayloadAction<number>) => {
        state.entityId = action.payload;
      },
      openEntityDetails: (state, action: PayloadAction<EntityDetailsPayload>) => {
        state.entityId = action.payload.id;
        state.isPublished = !action.payload.isDraft;
        state.lastModifiedDateTime = action.payload.dateModified;
      },
      ...reducers,
    },
  });
};
